import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  AccessTimeRounded,
  CalendarTodayRounded,
  InfoRounded,
  StarRounded,
  ErrorRounded,
  WarningRounded,
  CancelRounded,
  CloseRounded,
} from "@mui/icons-material";
import { useSubscription } from "./SubscriptionContext";
import { formatteDateDay } from "../../styles/styles";

const SubscriptionAlert = () => {
  const {
    subscription,
    hasActivePlan,
    isTrial,
    isSubscriptionEnding,
    isSubscriptionExpired,
    subscriptionEndedReason,
    expiryDate,
    subscriptionStatus,
    getPlanName,
    ready,
  } = useSubscription();

  // State for dismissing the alert
  const [isVisible, setIsVisible] = useState(true);
  const [isClosed, setIsClosed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const closedState = localStorage.getItem("subscriptionAlertClosed");
    if (closedState === "true") {
      setIsClosed(true);
      setIsVisible(false);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setIsClosed(true);
    localStorage.setItem("subscriptionAlertClosed", "true");

    // Auto-clear after 24 hours
    setTimeout(() => {
      localStorage.removeItem("subscriptionAlertClosed");
    }, 24 * 60 * 60 * 1000);
  };

  const alertConfig = useMemo(() => {
    if (!ready || !subscription || isClosed) return null;

    const planName = getPlanName();
    const now = new Date();

    // Calculate days until expiry
    const diffDays = expiryDate
      ? Math.ceil(
          (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        )
      : null;

    // Determine alert type and configuration
    if (isSubscriptionExpired || subscriptionEndedReason) {
      const isExpired =
        subscriptionEndedReason === "expired" || isSubscriptionExpired;
      const isCancelled = subscriptionEndedReason === "cancelled";

      return {
        severity: "critical",
        icon: isCancelled ? CancelRounded : ErrorRounded,
        label: isCancelled ? "Cancelled" : "Expired",
        message: isCancelled
          ? `Your ${isTrial ? "trial" : "subscription"} has been cancelled.`
          : `Your ${isTrial ? "trial" : "subscription"} has expired.`,
        countdownLabel: isCancelled ? "Cancelled" : "Expired",
        showCountdown: true,
        showDate: Boolean(expiryDate),
        planName: isTrial ? "Trial" : planName,
        bgColor: "#FFF5F5",
        borderColor: "#FED7D7",
        textColor: "#9B2C2C",
        iconColor: "#E53E3E",
        chipColor: "#E53E3E",
        gradient: "linear-gradient(135deg, #FFF5F5 0%, #FFF0F0 100%)",
      };
    }

    if (isSubscriptionEnding && diffDays !== null && diffDays >= 0) {
      const severity =
        diffDays <= 1 ? "critical" : diffDays <= 3 ? "warning" : "info";

      const countdownLabel =
        diffDays === 0
          ? "Today"
          : `${diffDays} day${diffDays === 1 ? "" : "s"}`;

      const message =
        diffDays === 0
          ? `Your ${isTrial ? "trial" : "subscription"} ends today.`
          : `Your ${
              isTrial ? "trial" : "subscription"
            } ends in ${countdownLabel}.`;

      // Color schemes based on severity
      const colorSchemes = {
        critical: {
          bgColor: "#FFF5F5",
          borderColor: "#FED7D7",
          textColor: "#9B2C2C",
          iconColor: "#E53E3E",
          chipColor: "#E53E3E",
          gradient: "linear-gradient(135deg, #FFF5F5 0%, #FFF0F0 100%)",
        },
        warning: {
          bgColor: "#FEF6E6",
          borderColor: "#FEEBC8",
          textColor: "#975A16",
          iconColor: "#DD6B20",
          chipColor: "#ED8936",
          gradient: "linear-gradient(135deg, #FEF6E6 0%, #FEF3E6 100%)",
        },
        info: {
          bgColor: "#EBF8FF",
          borderColor: "#BEE3F8",
          textColor: "#2C5282",
          iconColor: "#3182CE",
          chipColor: "#4299E1",
          gradient: "linear-gradient(135deg, #EBF8FF 0%, #E6F7FF 100%)",
        },
      };

      return {
        severity,
        icon: isTrial ? StarRounded : WarningRounded,
        label: isTrial ? "Trial" : planName,
        message,
        countdownLabel,
        showCountdown: true,
        showDate: Boolean(expiryDate),
        planName: isTrial ? "Trial" : planName,
        ...colorSchemes[severity],
      };
    }

    // Check for inactive subscription
    const hasActive = hasActivePlan();
    if (!hasActive && subscription) {
      return {
        severity: "warning",
        icon: InfoRounded,
        label: "Inactive",
        message: "Your subscription is currently inactive.",
        countdownLabel: "Inactive",
        showCountdown: false,
        showDate: false,
        planName: "Inactive Plan",
        bgColor: "#FEF6E6",
        borderColor: "#FEEBC8",
        textColor: "#975A16",
        iconColor: "#DD6B20",
        chipColor: "#ED8936",
        gradient: "linear-gradient(135deg, #FEF6E6 0%, #FEF3E6 100%)",
      };
    }

    // No alert needed
    return null;
  }, [
    ready,
    subscription,
    isTrial,
    isSubscriptionEnding,
    isSubscriptionExpired,
    subscriptionEndedReason,
    expiryDate?.getTime(),
    subscriptionStatus,
    isClosed,
  ]);

  if (!alertConfig) return null;

  const {
    icon: IconComponent,
    label,
    message,
    countdownLabel,
    showCountdown,
    showDate,
    planName,
    bgColor,
    borderColor,
    textColor,
    iconColor,
    chipColor,
    gradient,
  } = alertConfig;

  const tooltipContent = (
    <Box sx={{ textAlign: "center", px: 1, py: 0.5 }}>
      <Typography variant="body2" fontWeight="600" color={textColor}>
        {planName}
      </Typography>
      {showDate && expiryDate && (
        <Typography variant="caption" sx={{ opacity: 0.8, color: textColor }}>
          {subscriptionEndedReason ? "Ended" : "Expires"}:{" "}
          {formatteDateDay(expiryDate)}
        </Typography>
      )}
      {subscriptionStatus && (
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, display: "block", color: textColor }}
        >
          Status:{" "}
          {subscriptionStatus.charAt(0).toUpperCase() +
            subscriptionStatus.slice(1)}
        </Typography>
      )}
    </Box>
  );

  return (
    <Collapse in={isVisible}>
      <Box
        sx={{
          position: "fixed",
          bottom: "0rem",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 999,
          width: "auto",
          maxWidth: "90%",
          minWidth: "300px",
        }}
      >
        {/* <Tooltip title={tooltipContent} arrow> */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              p: "0.5rem 1rem",
              borderRadius: "10rem",
              background: gradient,
              border: `1px solid ${borderColor}`,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.25s ease",
              backdropFilter: "blur(6px)",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "5rem",
                backgroundColor: `${iconColor}15`,
              }}
            >
              <IconComponent sx={{ fontSize: 18, color: iconColor }} />
            </Box>

            {/* Content */}
            <Box
              sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}
            >
              {/* Plan Label Chip */}
              <Chip
                label={label}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  height: 24,
                  backgroundColor: chipColor,
                  color: "#ffffff",
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />

              {/* Message */}
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: textColor,
                  whiteSpace: "nowrap",
                }}
              >
                {message}
              </Typography>

              {/* Countdown */}
              {showCountdown && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTimeRounded sx={{ fontSize: 16, color: iconColor }} />
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: textColor,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {countdownLabel}
                  </Typography>
                </Box>
              )}

              {/* Date */}
              {showDate && expiryDate && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayRounded
                    sx={{ fontSize: 15, color: iconColor }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: textColor,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatteDateDay(expiryDate)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Close Button */}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{
                ml: 1,
                color: `${textColor}80`,
                "&:hover": {
                  backgroundColor: `${textColor}15`,
                  color: textColor,
                },
              }}
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        {/* </Tooltip> */}
      </Box>
    </Collapse>
  );
};

export default SubscriptionAlert;
